import React, { useRef, useEffect } from 'react';
import {
  DocumentTextIcon,
  LinkIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  PrinterIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import type { OverflowMenuProps, OverflowActionType } from './CaseHeader.types';

/**
 * Overflow menu items configuration
 */
const OVERFLOW_ITEMS: Array<{
  id: OverflowActionType;
  label: string;
  icon: React.ReactNode;
  dividerAfter?: boolean;
}> = [
  {
    id: 'billing',
    label: 'Facturation',
    icon: <DocumentTextIcon className="w-4 h-4" />,
  },
  {
    id: 'filemaker',
    label: 'FileMaker',
    icon: <LinkIcon className="w-4 h-4" />,
  },
  {
    id: 'expertise-detail',
    label: 'Détail expertise',
    icon: <ArrowTopRightOnSquareIcon className="w-4 h-4" />,
    dividerAfter: true,
  },
  {
    id: 'copy-link',
    label: 'Copier le lien',
    icon: <ClipboardDocumentIcon className="w-4 h-4" />,
  },
  {
    id: 'export',
    label: 'Exporter',
    icon: <DocumentTextIcon className="w-4 h-4" />,
  },
  {
    id: 'print',
    label: 'Imprimer',
    icon: <PrinterIcon className="w-4 h-4" />,
    dividerAfter: true,
  },
  {
    id: 'history',
    label: 'Historique',
    icon: <ClockIcon className="w-4 h-4" />,
  },
];

/**
 * OverflowMenu Component
 *
 * Three-dot menu for secondary actions like Products, Billing, FileMaker links.
 */
export const OverflowMenu: React.FC<OverflowMenuProps> = ({
  isOpen,
  onToggle,
  onSelect,
  onClose,
  affaireId,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Handle copy link
  const handleCopyLink = () => {
    const url = `${window.location.origin}/admin/tracker/${affaireId}`;
    navigator.clipboard.writeText(url);
    onClose();
  };

  const handleSelect = (id: OverflowActionType) => {
    if (id === 'copy-link') {
      handleCopyLink();
    } else {
      onSelect(id);
      onClose();
    }
  };

  return (
    <div className="case-header-overflow" ref={menuRef}>
      <button
        className="case-header-overflow-btn"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Plus d'options"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
        </svg>
      </button>

      {isOpen && (
        <div className="case-header-overflow-menu" role="menu">
          {OVERFLOW_ITEMS.map((item) => (
            <React.Fragment key={item.id}>
              <button
                className="case-header-overflow-item"
                onClick={() => handleSelect(item.id)}
                role="menuitem"
              >
                <span className="case-header-overflow-item-icon">{item.icon}</span>
                <span className="case-header-overflow-item-label">{item.label}</span>
              </button>
              {item.dividerAfter && <div className="case-header-overflow-divider" />}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default OverflowMenu;
